import React, { useCallback, useEffect, useState } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Modal from '@amzn/awsui-components-console/modal';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import {
  DraftStringInput,
  InlineMarkupType,
  MessageFormatType,
  StringInfo,
} from '../types';
import {
  CONTEXT_NOTE_CHAR_LIMIT,
  SOURCE_TEXT_CHAR_LIMIT,
  STRING_ID_FORMAT_ERROR_KEY,
  STRING_ID_MAX_LENGTH,
  STRING_ID_MAX_LENGTH_ERROR_KEY,
  STRING_ID_PATTERN,
} from '../constants';
import { includesStringId } from './createStringValidation';
import { isDraftAndStringIdentical } from 'src/features/string/utils/draftModified';
import NoPiiNotice from 'src/common/ui/NoPiiNotice';
import {
  ActiveChangeControlWindow,
  ChangeControlEditNotice,
} from 'src/common/ui/ChangeControlBanner';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import {
  INLINE_MARKUP_LABEL_IDS,
  MESSAGE_FORMAT_LABEL_IDS,
} from 'src/common/formatLabels';

interface EditStringModalProps {
  visible: boolean;
  stringInfo: StringInfo | null;
  sourceLocale: string;
  /** Whether this string is a draft-added string (not yet on the server). */
  isDraftAdded?: boolean;
  /**
   * The change-control window in effect, or `null` for a normal day (or when the
   * status could not be read, which fails open). When set, and the string is
   * already live, a notice under the no-PII notice says the edit will need an
   * MCM at submit. A draft-added string is not live yet and is exempt (design
   * §8.3), so the notice is suppressed for it regardless.
   */
  changeControlWindow?: ActiveChangeControlWindow | null;
  /** IDs of strings already committed on the server. Used for uniqueness validation. */
  existingStringIds?: string[];
  /** IDs of other draft-added strings. Used for uniqueness validation. */
  draftAddedStringIds?: string[];
  onSave: (draft: DraftStringInput, originalStringId?: string) => void;
  onDismiss: () => void;
}

interface FormErrors {
  stringId?: string;
  sourceText?: string;
  contextNote?: string;
}

const EditStringModal: React.FC<EditStringModalProps> = ({
  visible,
  stringInfo,
  sourceLocale,
  isDraftAdded = false,
  changeControlWindow = null,
  existingStringIds = [],
  draftAddedStringIds = [],
  onSave,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [stringId, setStringId] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [contextNote, setContextNote] = useState('');
  const [inlineMarkupType, setInlineMarkupType] = useState<InlineMarkupType>(
    InlineMarkupType.PlainText,
  );
  const [messageFormatType, setMessageFormatType] = useState<MessageFormatType>(
    MessageFormatType.None,
  );
  const [errors, setErrors] = useState<FormErrors>({});

  // Populate the form whenever the modal opens or targets a different string.
  // `visible` is a dependency because the parent keeps this component mounted
  // between opens: without it, reopening for the same `stringInfo` object would
  // skip the effect and show the previous session's unsaved edits.
  useEffect(() => {
    if (visible && stringInfo) {
      setStringId(stringInfo.stringId);
      setSourceText(stringInfo.sourceText);
      setContextNote(stringInfo.contextNote ?? '');
      setInlineMarkupType(
        stringInfo.inlineMarkupType ?? InlineMarkupType.PlainText,
      );
      setMessageFormatType(
        stringInfo.messageFormatType ?? MessageFormatType.None,
      );
      setErrors({});
    }
  }, [visible, stringInfo]);

  const handleDismiss = useCallback(() => {
    setErrors({});
    onDismiss();
  }, [onDismiss]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    // Validate the trimmed values because handleSave submits trimmed values,
    // keeping this flow's rules identical to CreateStringModal's.
    const trimmedStringId = stringId.trim();
    const trimmedSourceText = sourceText.trim();
    const trimmedContextNote = contextNote.trim();

    // Validate string ID format when editable (draft-added strings only)
    if (isDraftAdded) {
      if (!trimmedStringId) {
        newErrors.stringId = t('sgd-create-string-id-required');
      } else if (!STRING_ID_PATTERN.test(trimmedStringId)) {
        newErrors.stringId = t(STRING_ID_FORMAT_ERROR_KEY);
      } else if (trimmedStringId.length > STRING_ID_MAX_LENGTH) {
        newErrors.stringId = t(STRING_ID_MAX_LENGTH_ERROR_KEY, {
          max: STRING_ID_MAX_LENGTH,
        });
      } else if (trimmedStringId !== stringInfo?.stringId) {
        // Only check uniqueness when the ID was actually changed
        const allIds = [...existingStringIds, ...draftAddedStringIds];
        // Exclude the original ID (the string being edited) from the check
        const otherIds = allIds.filter((id) => id !== stringInfo?.stringId);
        if (includesStringId(otherIds, trimmedStringId)) {
          newErrors.stringId = t('sgd-create-string-id-duplicate-existing');
        }
      }
    }

    if (!trimmedSourceText) {
      newErrors.sourceText = t('sgd-create-source-text-required');
    } else if (trimmedSourceText.length > SOURCE_TEXT_CHAR_LIMIT) {
      newErrors.sourceText = t('sgd-source-text-too-long', {
        max: SOURCE_TEXT_CHAR_LIMIT,
      });
    }

    if (!trimmedContextNote) {
      newErrors.contextNote = t('sgd-create-context-note-required');
    } else if (trimmedContextNote.length > CONTEXT_NOTE_CHAR_LIMIT) {
      newErrors.contextNote = t('sgd-context-note-too-long', {
        max: CONTEXT_NOTE_CHAR_LIMIT,
      });
    }

    setErrors(newErrors);
    return (
      !newErrors.stringId && !newErrors.sourceText && !newErrors.contextNote
    );
  };

  const handleSave = () => {
    if (!stringInfo || !validate()) {
      return;
    }

    const trimmedStringId = isDraftAdded
      ? stringId.trim()
      : stringInfo.stringId;
    const originalStringId = stringInfo.stringId;

    const draft: DraftStringInput = {
      stringId: trimmedStringId,
      sourceLocale,
      sourceText: sourceText.trim(),
      contextNote: contextNote.trim(),
      inlineMarkupType,
      messageFormatType,
      variants: stringInfo.variants,
    };

    if (!isDraftAndStringIdentical(draft, stringInfo)) {
      // Pass originalStringId so the caller can handle renames for draft-added strings
      onSave(draft, originalStringId);
      onDismiss();
    } else {
      onDismiss();
    }
  };

  if (!stringInfo) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={t('sgd-edit-string')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              {t('string-cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {t('sgd-save')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <NoPiiNotice />
        <ChangeControlEditNotice
          changeControlWindow={isDraftAdded ? null : changeControlWindow}
        />

        <ColumnLayout columns={2}>
          <FormField
            label={t('string-field-string-id')}
            errorText={errors.stringId}
          >
            {isDraftAdded ? (
              <Input
                value={stringId}
                onChange={({ detail }) => {
                  setStringId(detail.value);
                  setErrors((prev) => ({ ...prev, stringId: undefined }));
                }}
              />
            ) : (
              <Input value={stringInfo.stringId} disabled />
            )}
          </FormField>
          <FormField label={t('string-field-source-locale')}>
            <Input value={sourceLocale} disabled />
          </FormField>
        </ColumnLayout>

        <FormField
          label={t('string-field-source-text-required')}
          errorText={errors.sourceText}
          constraintText={t('field-character-limit', {
            limit: SOURCE_TEXT_CHAR_LIMIT,
          })}
        >
          <Textarea
            value={sourceText}
            onChange={({ detail }) => {
              setSourceText(detail.value);
              setErrors((prev) => ({ ...prev, sourceText: undefined }));
            }}
          />
        </FormField>

        <FormField
          label={t('string-field-context-note-required')}
          errorText={errors.contextNote}
          constraintText={t('field-character-limit', {
            limit: CONTEXT_NOTE_CHAR_LIMIT,
          })}
        >
          <Textarea
            value={contextNote}
            rows={3}
            onChange={({ detail }) => {
              setContextNote(detail.value);
              setErrors((prev) => ({ ...prev, contextNote: undefined }));
            }}
          />
        </FormField>

        <FormField label={t('string-field-inline-markup-required')}>
          <RadioGroup
            value={inlineMarkupType}
            items={[
              {
                value: InlineMarkupType.PlainText,
                label: t(INLINE_MARKUP_LABEL_IDS[InlineMarkupType.PlainText]),
              },
              {
                value: InlineMarkupType.Html,
                label: t(INLINE_MARKUP_LABEL_IDS[InlineMarkupType.Html]),
              },
              {
                value: InlineMarkupType.Markdown,
                label: t(INLINE_MARKUP_LABEL_IDS[InlineMarkupType.Markdown]),
              },
            ]}
            onChange={({ detail }) =>
              setInlineMarkupType(detail.value as InlineMarkupType)
            }
          />
        </FormField>

        <FormField label={t('string-field-message-format-required')}>
          <RadioGroup
            value={messageFormatType}
            items={[
              {
                value: MessageFormatType.None,
                label: t(MESSAGE_FORMAT_LABEL_IDS[MessageFormatType.None]),
              },
              {
                value: MessageFormatType.HashGet,
                label: t(MESSAGE_FORMAT_LABEL_IDS[MessageFormatType.HashGet]),
              },
              {
                value: MessageFormatType.AmazonMessageFormat,
                label: t(
                  MESSAGE_FORMAT_LABEL_IDS[
                    MessageFormatType.AmazonMessageFormat
                  ],
                ),
              },
              {
                value: MessageFormatType.Mustache,
                label: t(MESSAGE_FORMAT_LABEL_IDS[MessageFormatType.Mustache]),
              },
            ]}
            onChange={({ detail }) =>
              setMessageFormatType(detail.value as MessageFormatType)
            }
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default EditStringModal;
